import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import type {FinancialProductCalculation} from '../schemas/FinancialProductCalculationSchema';
import type {DisplayedFinancialProductQuote} from '../types/DisplayedFinancialProductQuote';
import type {FinancialProductQuoteCoordinates} from '../types/FinancialProductQuoteCoordinates';
import type {FinancialProductQuoteEnvelope} from '../types/FinancialProductQuoteEnvelope';
import type {FinancialProductQuoteOffer} from '../types/FinancialProductQuoteOffer';
import {getLocalFinancialProductQuote} from './getLocalFinancialProductQuote';

const isCalculationAt = (
  calculation: FinancialProductCalculation,
  coordinates: FinancialProductQuoteCoordinates
) =>
  calculation.termMonths === coordinates.termMonths &&
  (coordinates.productType === 'LEASING'
    ? calculation.leasingTargetGroup === (coordinates.leasingTargetGroup ?? null) &&
      calculation.annualMileage === (coordinates.annualMileage ?? null)
    : calculation.downPaymentPercentage === (coordinates.downPaymentPercentage ?? null) &&
      calculation.balloonPaymentPercentage === (coordinates.balloonPaymentPercentage ?? null));

/**
 * The result a panel shows for these coordinates. The stored calculation is the quote of record
 * and wins whenever it prices the same coordinates. A contract snapshot is never priced locally
 * (it shows stored values only). Otherwise the local quote fills the gap until the backend
 * answers, or NONE asks for a placeholder because the inputs are withheld, from an older backend,
 * or the coordinates are incomplete.
 */
export function getDisplayedFinancialProductQuote(props: {
  envelope: FinancialProductQuoteEnvelope;
  offer: (FinancialProductQuoteOffer & {calculation: FinancialProductCalculation | null}) | null;
  coordinates: FinancialProductQuoteCoordinates;
}): DisplayedFinancialProductQuote {
  const offer = props.offer;
  const storedCalculation =
    isNotNil(offer) &&
    isNotNil(offer.calculation) &&
    offer.productType === props.coordinates.productType &&
    isCalculationAt(offer.calculation, props.coordinates)
      ? offer.calculation
      : null;

  return match({offer, storedCalculation, mode: props.envelope.mode})
    .returnType<DisplayedFinancialProductQuote>()
    .with({storedCalculation: Pattern.nonNullable}, ({storedCalculation: calculation}) => ({
      source: 'STORED',
      quote: {availability: 'AVAILABLE', unavailableReason: null, calculation},
    }))
    .with({offer: Pattern.nonNullable, mode: 'LIVE'}, (live) => {
      const quote = getLocalFinancialProductQuote(props.envelope, live.offer, props.coordinates);

      return isNil(quote) ? {source: 'NONE'} : {source: 'LOCAL', quote};
    })
    .otherwise(() => ({source: 'NONE'}));
}
